import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ISelectOption, MarketSortByType } from "../../../common/commonTypes";
import { marketSortOptions } from "../../../common/marketSortOptions";
import { getParagraph14BoldTypography } from "../../../common/Typography";
import useMobileResponsive from "../../../common/hooks/useMobileResponsive";
import Typography from "../../../common/components/Typography/Typography";
import Switch from "../../../common/components/Switch/Switch";
import Select from "../../../common/components/Select/Select";
import Icon from "../../../common/components/Icon/Icon";

const Root = styled.div`
  padding-top: 20px;
`;

const FilterSortWeb = styled.div``;

const FilterWebSelectContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 24px;
`;

const FilterSortMobile = styled.div`
  display: grid;
  grid-template-rows: auto auto auto;
  row-gap: 24px;
`;

const FilterSortMobileContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileS}) {
    display: grid;
    grid-template-columns: auto;
    grid-template-rows: auto auto;
    row-gap: 24px;
  }
`;

const FilterButton = styled.button`
  height: 44px;
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.1);
  box-sizing: border-box;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 11px 0 16px;
`;

const FilterButtonContent = styled.div`
  display: flex;
  align-items: center;
`;

const FilterIcon = styled(Icon)`
  padding-right: 6px;
`;

const FilterButtonLabel = styled.p`
  color: ${({ theme }) => theme.primaryColor};
  ${getParagraph14BoldTypography};
  line-height: 16px;
`;

const FilterIndicator = styled.div`
  width: 18px;
  height: 18px;
  background: ${({ theme }) => theme.accent.normal};
  border-radius: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FilterIndicatorNumber = styled.div`
  color: ${({ theme }) => theme.primaryColor};
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-top: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding-top: 0px;
  }
`;

const ToggleLabel = styled(Typography)`
  opacity: 0.7;
  margin-left: 10px;
`;

const StyledSelect = styled(Select)`
  .market-filter-select__control {
    height: 34px !important;
  }
`;

type Props = {
  lang?: string;
  genre?: string;
  sortBy: MarketSortByType;
  languages: ISelectOption[];
  genres: ISelectOption[];
  isForSale: boolean;
  onToggleForSale: (toggle: boolean) => void;
  onSelectLang: (option: any) => void;
  onSelectGenre: (option: any) => void;
  onSelectSortBy: (option: any) => void;
  onResetFilters: () => void;
};

const FilterSort = ({
  lang,
  genre,
  sortBy,
  languages,
  genres,
  isForSale,
  onToggleForSale,
  onSelectLang,
  onSelectGenre,
  onSelectSortBy,
  onResetFilters,
}: Props) => {
  const { t } = useTranslation();
  const { isTablet } = useMobileResponsive();

  const activeFiltersCount = [lang, genre, isForSale].filter(
    (value) => !!value
  ).length;

  return (
    <Root>
      {isTablet ? (
        <FilterSortMobile>
          <FilterSortMobileContainer>
            <FilterButton onClick={onResetFilters}>
              <FilterButtonContent>
                <FilterIcon
                  size="22px"
                  name={
                    activeFiltersCount > 0 ? "filterCloseIcon" : "filterIcon"
                  }
                />
                <FilterButtonLabel>
                  {t("market-filter-button-label")}
                </FilterButtonLabel>
              </FilterButtonContent>
              {(lang || genre || isForSale) && (
                <FilterIndicator>
                  <FilterIndicatorNumber>
                    {activeFiltersCount}
                  </FilterIndicatorNumber>
                </FilterIndicator>
              )}
            </FilterButton>
            <StyledSelect
              classNamePrefix={"market-filter-select"}
              options={marketSortOptions}
              value={sortBy}
              onChange={onSelectSortBy}
              placeholder={t("market-filter-sortBy-placeholder")}
            />
          </FilterSortMobileContainer>

          <StyledSelect
            classNamePrefix={"market-filter-select"}
            options={languages}
            value={lang}
            onChange={onSelectLang}
            placeholder={t("market-filter-language-placeholder")}
          />
          <StyledSelect
            classNamePrefix={"market-filter-select"}
            options={genres}
            value={genre}
            onChange={onSelectGenre}
            placeholder={t("market-filter-genre-placeholder")}
          />
          <ToggleWrapper>
            <Switch
              id="sale"
              height="20px"
              width={"40px"}
              size={14}
              top={3}
              left={3}
              toggled={isForSale}
              onChange={onToggleForSale}
            />
            <ToggleLabel size="md" weight="500">
              {t("market-for-sale-toggle")}
            </ToggleLabel>
          </ToggleWrapper>
        </FilterSortMobile>
      ) : (
        <FilterSortWeb>
          <FilterWebSelectContainer>
            <StyledSelect
              classNamePrefix={"market-filter-select"}
              options={languages}
              value={lang}
              onChange={onSelectLang}
              placeholder={t("market-filter-language-placeholder")}
            />

            <StyledSelect
              classNamePrefix={"market-filter-select"}
              options={genres}
              value={genre}
              onChange={onSelectGenre}
              placeholder={t("market-filter-genre-placeholder")}
            />

            <StyledSelect
              classNamePrefix={"market-filter-select"}
              options={marketSortOptions}
              value={sortBy}
              onChange={onSelectSortBy}
              placeholder={t("market-filter-sortBy-placeholder")}
            />
          </FilterWebSelectContainer>
          <ToggleWrapper>
            <Switch
              id="sale"
              height="20px"
              width={"40px"}
              size={14}
              top={3}
              left={3}
              toggled={isForSale}
              onChange={onToggleForSale}
            />
            <ToggleLabel size="md" weight="500">
              {t("market-for-sale-toggle")}
            </ToggleLabel>
          </ToggleWrapper>
        </FilterSortWeb>
      )}
    </Root>
  );
};

export default FilterSort;
